import {
  addDays,
  differenceInCalendarDays,
  format,
  setHours,
  setMinutes,
  startOfDay,
  startOfWeek,
} from 'date-fns'
import {DefaultRecourse, ProcessedEvent, TemplateDraft} from '../types'
import {LaborTemplateModel, LaborTemplateShiftModel} from '../core/_models'
import {getEventColor, getPositionColor, getPositionDisplayName} from './generals'

// Template rows are placeholder resources keyed by this prefix + row index —
// the grid keys everything on resourceFields.idField ('employee_id'), so rows
// need unique ids that can never collide with real employee ids (numbers).
export const TEMPLATE_ROW_PREFIX = 'template-row-'

export const DEFAULT_TEMPLATE_ROWS = 10

export const templateRowId = (index: number) => `${TEMPLATE_ROW_PREFIX}${index}`

export const templateRowIndex = (employeeId: unknown): number | null => {
  if (typeof employeeId !== 'string' || !employeeId.startsWith(TEMPLATE_ROW_PREFIX)) return null
  const index = Number(employeeId.slice(TEMPLATE_ROW_PREFIX.length))
  return Number.isInteger(index) && index >= 0 ? index : null
}

// Templates have no dates, but the grid, editor, and hours-of-operation
// lookups all operate on real Dates — so the draft anchors day-of-week onto
// next week's Monday (ISO 0=Mon..6=Sun, matching the API's day_of_week).
// All 7 anchor days are strictly in the future, which keeps the grid's
// past-date cell disabling out of the way. The anchor week begins on the
// COMPANY's week start (date-fns 0=Sun..6=Sat, from the store's week config)
// so the weekly tabs mirror the real schedule's day order.
export const getTemplateAnchorStart = (weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 1) =>
  startOfWeek(addDays(new Date(), 7), {weekStartsOn})

// ISO day-of-week (0=Mon..6=Sun) — the API's day_of_week convention, which is
// deliberately independent of the company week start.
export const isoDayOfWeek = (date: Date) => (date.getDay() + 6) % 7

// The anchor-week date carrying a stored ISO day_of_week. With a non-Monday
// week start this is NOT anchor+dayOfWeek — search the 7 anchor days.
export const templateDayDate = (anchorStart: Date, dayOfWeek: number | null) => {
  if (dayOfWeek === null || dayOfWeek === undefined) return anchorStart
  for (let i = 0; i < 7; i++) {
    const day = addDays(anchorStart, i)
    if (isoDayOfWeek(day) === dayOfWeek) return day
  }
  return anchorStart
}

// Tab index (0..6 from the anchor week start) of a date.
export const templateDayIndex = (anchorStart: Date, date: Date) =>
  differenceInCalendarDays(startOfDay(date), anchorStart)

export const generateEventId = () => Date.now().toString(36) + Math.random().toString(36).slice(2)

// The day a draft event belongs to. businessDay is what the grid filters on,
// so row labels must agree with it or a row can label itself off a shift the
// visible day doesn't show.
export const eventDayTime = (event: ProcessedEvent) =>
  startOfDay(event.businessDay || event.start).getTime()

// Row resources for the template grid. A row is labelled ONLY by a positioned
// shift on the active (visible) day — on a weekly template every tab is an
// independent day, so a row positioned on Monday is back to "Row N" on Tuesday
// until it gets a shift there. Rows with no position render as "Row N" in
// Timeline, which keys off title/position both being blank.
export const makeTemplateRowResources = (
  rowCount: number,
  events: ProcessedEvent[],
  activeDate: Date
): DefaultRecourse[] => {
  const rows: DefaultRecourse[] = []
  const activeDayTime = startOfDay(activeDate).getTime()
  for (let i = 0; i < rowCount; i++) {
    const rowId = templateRowId(i)
    const activeDayEvent = events.find(
      (ev) => ev.employee_id === rowId && ev.position && eventDayTime(ev) === activeDayTime
    )
    const position = activeDayEvent?.position || ''
    rows.push({
      employee_id: rowId,
      title: '',
      position: position,
      position_name: position ? getPositionDisplayName(position) : '',
      color: position ? getPositionColor(position) : '',
    })
  }
  return rows
}

// Draft events → API shift payloads. Rows with no shifts simply don't
// serialize; the row layout is reconstructed from row_index on load.
// Overnight shifts keep their natural times: end 'HH:mm' <= start 'HH:mm'
// is the crosses-midnight convention (+1 day at apply time).
export const eventsToTemplateShifts = (
  events: ProcessedEvent[],
  templateType: 'daily' | 'weekly',
  anchorStart: Date
): LaborTemplateShiftModel[] => {
  return events
    .map((ev): LaborTemplateShiftModel | null => {
      const rowIndex = templateRowIndex(ev.employee_id)
      if (rowIndex === null) return null
      const day = ev.businessDay ? startOfDay(ev.businessDay) : startOfDay(ev.start)
      return {
        // ISO dow from the actual date — NOT the tab index, which only equals
        // it when the company week starts on Monday.
        day_of_week: templateType === 'weekly' ? isoDayOfWeek(day) : null,
        row_index: rowIndex,
        position: ev.position || null,
        start_time: format(ev.start, 'HH:mm'),
        end_time: format(ev.end, 'HH:mm'),
        // Dateless like the shift times: applying re-derives which day each
        // one falls on from whether it precedes start_time.
        break_start_time: ev.break_start ? format(ev.break_start, 'HH:mm') : null,
        break_end_time: ev.break_end ? format(ev.break_end, 'HH:mm') : null,
      }
    })
    .filter((s): s is LaborTemplateShiftModel => s !== null)
}

// Stored times are dateless clock times, but the two directions don't agree on
// the string: we write 'HH:mm', while the API serializes its `time` columns as
// 'HH:mm:ss'. date-fns `parse(value, 'HH:mm', day)` rejects the trailing ':ss'
// with an Invalid Date, which stays silent until a `format` call somewhere in
// the grid throws RangeError — so read the leading hour/minute ourselves and
// build the date, accepting either shape.
const CLOCK_TIME = /^(\d{1,2}):(\d{2})/

const parseTemplateTime = (value: string | null | undefined, day: Date): Date | null => {
  const match = CLOCK_TIME.exec((value || '').trim())
  if (!match) return null
  const [hours, minutes] = [Number(match[1]), Number(match[2])]
  if (hours > 23 || minutes > 59) return null
  return setMinutes(setHours(startOfDay(day), hours), minutes)
}

// Stored template → draft events on the anchor week.
export const templateShiftsToEvents = (
  template: LaborTemplateModel,
  anchorStart: Date
): ProcessedEvent[] => {
  return template.shifts
    .map((shift): ProcessedEvent | null => {
      const day = templateDayDate(anchorStart, shift.day_of_week)
      const start = parseTemplateTime(shift.start_time, day)
      let end = parseTemplateTime(shift.end_time, day)
      // Every downstream consumer assumes a drawable event: drop the row
      // rather than hand the grid an Invalid Date it can only crash on.
      if (!start || !end) {
        console.warn('Skipping template shift with unreadable times', shift)
        return null
      }
      if (end <= start) {
        end = addDays(end, 1) // crosses midnight
      }
      // Same unroll rule the server applies: a break earlier in the clock than
      // the shift's start belongs to the rolled-over day.
      const unroll = (value: string | null | undefined): Date | null => {
        const parsed = parseTemplateTime(value, day)
        if (!parsed) return null
        return parsed < start ? addDays(parsed, 1) : parsed
      }
      const breakStart = unroll(shift.break_start_time)
      const breakEnd = unroll(shift.break_end_time)
      return {
        event_id: generateEventId(),
        title: shift.position ? getPositionDisplayName(shift.position) : '',
        position: shift.position || '',
        start: start,
        end: end,
        businessDay: day,
        allDay: false,
        employee_id: templateRowId(shift.row_index),
        // Same helper the store's confirmEvent uses, so a position-less shift
        // keeps the SAME color whether it was just created or reloaded.
        // Leaving this undefined made loaded shifts fall through to MUI's theme
        // primary while created ones got the position default — two blues.
        color: getEventColor(shift.position),
        // Breaks are both-or-neither everywhere else; half a break would
        // serialize back as an invalid payload.
        break_start: breakStart && breakEnd ? breakStart : null,
        break_end: breakStart && breakEnd ? breakEnd : null,
      }
    })
    .filter((ev): ev is ProcessedEvent => ev !== null)
}

// Row count to show for a loaded template: at least the default 10, and
// enough to fit every stored row.
export const templateRowCount = (template: LaborTemplateModel): number => {
  const maxRowIndex = template.shifts.reduce((max, s) => Math.max(max, s.row_index), -1)
  return Math.max(DEFAULT_TEMPLATE_ROWS, maxRowIndex + 1)
}

export const buildTemplateDraft = (template: LaborTemplateModel): TemplateDraft => ({
  uuid: template.uuid,
  name: template.name,
  template_type: template.template_type,
  rowCount: templateRowCount(template),
})
